import axios from 'axios';
import { z } from 'zod';

import { strictQuery } from 'utils/strict-query';

import { AddressSuggestionZod } from 'types/zod/address-suggestion';
import { AdvisorParticipantZod } from 'types/zod/advisor-participant';
import { AdvisorsGroupZod } from 'types/zod/advisors-group';
import { CommunityZod } from 'types/zod/community';
import { CurrentParticipantZod } from 'types/zod/current-participant';
import { ParticipantZod } from 'types/zod/participant';
import { ParticipantReferencesZod } from 'types/zod/participant-references';
import { TempUserZod } from 'types/zod/temp-user';
import { UserSettingsZod } from 'types/zod/user-settings';

const ParticipantsApi = {
    createParticipant: strictQuery({
        In: ParticipantZod.partial(),
        Out: ParticipantZod,
        handler: (participant) => axios.post('/participants', participant),
    }),

    deleteObserver: strictQuery({
        In: ParticipantZod.shape.participant_id,
        Out: z.any(),
        handler: (id) => axios.patch(`/participants/${id}/delete-observer`),
    }),

    findParticipant: strictQuery({
        In: z.object({
            email: z.string().email().optional(),
            lastname: z.string().optional(),
            dob: z.string().optional(),
        }),
        Out: z.array(ParticipantZod),
        handler: ({ email, lastname, dob }) =>
            axios.get('/participants', { params: { email, 'last-name': lastname, dob } }),
    }),

    getAdvisorById: strictQuery({
        In: ParticipantZod.shape.participant_id,
        Out: AdvisorParticipantZod,
        handler: (id) => axios.get(`/participants/advisors/${id}`),
    }),

    getAdvisors: strictQuery({
        In: z
            .object({ groupId: AdvisorsGroupZod.shape.groupId.optional(), recursively: z.boolean().optional() })
            .optional(),
        Out: z.array(AdvisorParticipantZod),
        handler: (params) => axios.get('/participants/advisors', { params }),
    }),

    getCommunity: strictQuery({
        In: ParticipantZod.shape.participant_id.optional(),
        Out: CommunityZod,
        handler: (id) => axios.get(`/participants/community`, { params: { participant_id: id } }),
    }),

    getFolderPath: strictQuery({
        In: z.string().email(),
        Out: z.object({ result: z.string() }),
        handler: (email) => axios.get('/participants/s3folder-by-email', { params: { email } }),
    }),

    getMe: strictQuery({
        In: z.void(),
        Out: CurrentParticipantZod,
        handler: () => axios.get('/me'),
    }),

    getParticipant: strictQuery({
        In: z.number(),
        Out: ParticipantZod,
        handler: (id) => axios.get(`/participants/${id}`),
    }),

    getAdvisorParticipant: strictQuery({
        In: z.number(),
        Out: AdvisorParticipantZod,
        handler: (id) => axios.get(`/participants/${id}`),
    }),

    getParticipants: strictQuery({
        In: z.void(),
        Out: z.array(ParticipantZod),
        handler: () => axios.get('/participants'),
    }),

    getTempUser: strictQuery({
        In: z.void(),
        Out: TempUserZod,
        handler: () => axios.get('/participants/temp-user'),
    }),

    isDirectInvoicing: strictQuery({
        In: z.void(),
        Out: z.any(),
        handler: () => axios.get('/participants/is-direct-invoicing'),
    }),

    linkRegisteredUser: strictQuery({
        mapResponse: ({ data, status }) => ({ data, status }),
        In: z.string(),
        Out: z.union([
            z.object({ data: z.union([ParticipantZod.shape.email, ParticipantZod.shape.phone_number]), status: z.literal(202) }),
            z.object({ data: z.string(), status: z.literal(204) }),
        ]),
        handler: (invite) => axios.patch(`/participants/link`, null, { params: { invite } }),
    }),

    lookupAddresses: strictQuery({
        In: z.object({ search: z.string(), state: z.string().optional() }),
        Out: z.object({ suggestions: z.array(AddressSuggestionZod).optional() }),
        handler: ({ search, state }) => axios.get('/addresses/lookup', { params: { search, state } }),
    }),

    resendNotifications: strictQuery({
        In: z.object({ id: ParticipantZod.shape.participant_id, params: z.any() }),
        Out: z.object({}),
        handler: ({ id, params }) => axios.patch(`/participants/${id}/notify`, null, params),
    }),

    sendInvitation: strictQuery({
        In: z.string(),
        Out: z.any(),
        handler: (invite) => axios.patch(`/participants/invite`, invite),
    }),

    setMailingAddress: strictQuery({
        In: z.object({ id: ParticipantZod.shape.participant_id, mailing: z.any() }),
        Out: z.any(),
        handler: ({ id, mailing }) => axios.put(`/participants/${id}/address`, { mailing }),
    }),

    setNewUserEmail: strictQuery({
        In: z.string().email(),
        Out: z.object({ success: z.boolean() }),
        handler: (email) => axios.patch(`/participants/send-update-email-link`, {}, { params: { email } }),
    }),

    updateEmail: strictQuery({
        In: z.string(),
        Out: z.any(),
        handler: (eventUuid) => axios.post(`/participants/update-email`, null, { params: { eventUuid } }),
    }),

    updateParticipant: strictQuery({
        In: ParticipantZod.partial().and(
            z.object({ community: z.object({ notify: z.boolean(), share: z.number().array() }) }).partial(),
        ),
        Out: ParticipantZod,
        handler: (participant) => axios.put(`/participants/${participant.participant_id}`, participant),
    }),

    updateParticipants: strictQuery({
        In: z.array(ParticipantZod.partial()),
        Out: z.any(),
        handler: (participants) => axios.put(`/participants/`, participants),
    }),

    verifyLinking: strictQuery({
        mapResponse: ({ data, status }) => ({ data, status }),
        In: z.string(),
        Out: z.object({ data: z.any(), status: z.number() }),
        handler: (code) => axios.patch(`/participants/${code}/link`),
    }),

    updateUserSettings: strictQuery({
        In: z.object({ participant_id: ParticipantZod.shape.participant_id, settings: UserSettingsZod }),
        Out: z.any(),
        handler: ({ participant_id, settings }) => axios.put(`/participants/${participant_id}/user-settings`, settings),
    }),

    getParticipantReferences: strictQuery({
        In: z.void(),
        Out: ParticipantReferencesZod,
        handler: () => axios.get(`/participants/references`),
    }),

    removeParticipant: strictQuery({
        In: ParticipantZod.shape.participant_id,
        Out: z.void(),
        handler: (id) => axios.delete(`/participants/${id}`),
    }),
};

export default ParticipantsApi;
